//** React Imports
import React, { useState, useEffect, useContext, createContext } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import apiFetech from "../configs/axios";
import { AuthServices } from "../services/AuthServices";
import { getClientLocation, getInfoFromGoogleMap } from "../utils";
import { useClientCity } from "./useClientCity";
import { useCountries } from "./useCountries";
import { useLanguage } from "./useLanguage";

// ** Create Context
const authContext = createContext();

// Provider component that wraps your app and makes Auth object ...
// ... available to any child component that calls useAuth().
export function ProvideAuth({ children }) {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
}

// Hook for child components to get the Auth object ...
// ... and re-render when it changes.
export const useAuth = () => {
  return useContext(authContext);
};
// ** if screent url in this array not Redirect url =>not-supported
const routesNotRedirectNotSupported = [
  '/onboarding',
  '/location',
  '/login',
  '/forgot-password',
  '/register'
]

// Provider hook that creates Auth object and handles state
function useProvideAuth() {
  // ** React Router Hooks
  const location = useLocation();
  let navigate = useNavigate();
  const { t } = useTranslation();
  const { language } = useLanguage();
  const { countries, cities,getCoutries } = useCountries();
  const { saveClientCity, clientCity } = useClientCity();

  const [isStartGetLocation, setIsStartGetLocation] = useState(false)

  // ** States
  const [userData, setUserData] = useState({});
  const [userLocation, setUserLocation] = useState({});
  const [userSecure, setUserSecure] = useState({});
  const [IsSupportedAppLoading, setIsSupportedAppLoading] = useState(true);

  // ** Set & Save Token
  const saveToken = ({ access_token, token_type = "Bearer" }) => {
    localStorage.setItem(
      "secure",
      JSON.stringify({ access_token, token_type })
    );
    setUserSecure({ access_token, token_type });
  };
  // ** Set & Save User Info
  const saveUser = ({ user }) => {
    localStorage.setItem("user", JSON.stringify(user));
    setUserData(user);
  };

  // ** get Token
  const getToken = () => {
    const data = localStorage.getItem("secure")
      ? JSON.parse(localStorage.getItem("secure"))
      : {};
    setUserSecure(data);
  };

  // ** Set & Save User Info
  const saveUserLocation = (userDataLocation) => {
    localStorage.setItem("userLocation", JSON.stringify(userDataLocation));
    setUserLocation({ ...userDataLocation });
  };

  // Get Profile Data
  const getProfileData = (userToken) => {
    if (userToken) {
      AuthServices.getProfile(userToken)
        .then((result) => {
          const { data } = result;
          if ("data" in data) {
            const user = data.data;
            if ("local_lar" in user) {
              user.local_lat = user.local_lar;
            }
            // setUserData(data.Customer[0]);
            saveUser({ user });
            // localStorage.setItem("user", JSON.stringify(user));
          }
        })
        .catch((error) => {});
    }
  };

  // ** Handle User signup
  const signUp = (formData) => {
    return new Promise((resolve, reject) => {
      AuthServices.signup(formData)
        .then((result) => {
          const { data } = result;
          resolve({ data });
        })
        .catch((error) => {
          reject({ errors: error });
        });
    });
  };

  // ** Handle Upadate Profile
  const updateProfile = ({ formData }) => {
    return new Promise((resolve, reject) => {
      AuthServices.updateProfile({ formData })
        .then((result) => {
          const { data } = result;
          if (Object.keys(data).length > 0) {
            getProfileData(userSecure.access_token);
            resolve({ update: true });
          } else {
            resolve({ update: false });
          }
        })
        .catch((error) => {
          reject({ errors: error });
        });
    });
  };

  // ** Handle User Login
  const handleLogin = (formData) => {
    return new Promise((resolve, reject) => {
      AuthServices.signin(formData)
        .then((result) => {
          const { data } = result;
          saveToken({
            access_token: data.access_token,
            token_type: data.token_type
          });
          getProfileData(data.access_token);
          resolve({ data });
        })
        .catch((error) => {
          reject({ errors: error });
        });
    });
  };

  // ** Handle User SignOut
  const handleSignOut = async () => {
    return new Promise((resolve, reject) => {
      const token = userSecure?.access_token || null;
      AuthServices.signOut({ token: token })
        .then(async (result) => {
          const { data } = result;
          if (data) {
            await localStorage.removeItem("user");
            await localStorage.removeItem("secure");
            setUserData({});
            resolve({ signOut: { success: true, message: data.message } });
          }
        })
        .catch((error) => {
          toast.warn(t("Oops Error!"));
          navigate("/profile");
        });
    });
  };

  function onAuthStateChanged(callback) {
    if (localStorage.getItem("user")) {
      let user =
        localStorage.getItem("user") !== "undefined" &&
        JSON.parse(localStorage.getItem("user"));
      callback(user);
    } else {
      // localStorage.removeItem("user");
      callback(null);
    }
  }
  // ** Send Message
  const sedMessage = (formData) => {
    return new Promise((resolve, reject) => {
      if (userSecure.access_token) {
        AuthServices.sendMessage({
          token: userSecure.access_token,
          formData: formData
        })
          .then((result) => {
            if ("Ticket" in result?.data) {
              resolve(result);
            } else {
              reject("error");
            }
          })
          .catch((error) => {
            reject(error);
          });
      } else {
        navigate("/login");
      }
    });
  };

  const clientSaveLocation = ({ cleanData, currentPosition }) => {
    const {
      street_address,
      route,
      locality,
      administrative_area_level_1,
      country,
      cityInfo
    } = cleanData;

    const fullAddress = {
      locality: null,
      street_address: null,
      administrative_area_level_1: null,
      route: null
    };
    if (locality && locality?.formatted_address) {
      fullAddress.locality = locality?.formatted_address;
    }
    if (street_address?.formatted_address) {
      fullAddress.street_address = street_address?.formatted_address;
    }
    if (
      administrative_area_level_1 &&
      administrative_area_level_1?.formatted_address
    ) {
      fullAddress.administrative_area_level_1 =
        administrative_area_level_1?.formatted_address;
    }
    if (route && route?.formatted_address) {
      fullAddress.route = route?.formatted_address;
    }
    if (country && country?.formatted_address) {
      fullAddress.country = country?.formatted_address;
    }

    try {
      const cityObj = { name: null };
      if (fullAddress?.route) {
        cityObj.name = fullAddress?.route;
      } else if (fullAddress?.locality) {
        cityObj.name = fullAddress?.locality;
      } else if (fullAddress?.street_address) {
        cityObj.name = fullAddress?.street_address;
      } else if (fullAddress?.administrative_area_level_1) {
        cityObj.name = fullAddress?.administrative_area_level_1;
      }
      const city = cities.find((c) => {
        const cityName = cityObj.name;
        return cityName.toUpperCase().includes(c?.name?.toUpperCase());
      });
      if (city) {
        saveClientCity(city);
      } else {
        saveClientCity({ id: 0, country_id: 0 });
      }
      const data = {
        ...currentPosition,
        fullAddress: { ...fullAddress },
        cityInfo
      };
      saveUserLocation(data);
    } catch (error) {
      const data = {
        ...currentPosition,
        fullAddress: { ...fullAddress }
      };
      saveUserLocation(data);
    }
  };

  // ** Is supported app on the city
  const appIsCitySupported = () => {
    if (cities.length > 0 && Object.keys(clientCity).length > 0) {
      const city = cities.find((c) => {
        return c?.id === clientCity?.id;
      });
      if (city) {
        return true;
      }
      return false;
    }
  };

  // ** Make Unique Code if exists return unique code or make new
  const makeUniqueCode = () => {
    if (localStorage.getItem("uniqueCode") !== null) {
      return localStorage.getItem("uniqueCode");
    } else {
      const uniqueCode = `gootech_${Date.now()}`;
      localStorage.setItem("uniqueCode", uniqueCode);
      return uniqueCode;
    }
  };

  // ** Forget Password
  const forgetPassword = ({ email }) => {
    return new Promise((resolve, reject) => {
      AuthServices.forgetPassword({ email: email })
        .then((result) => {
          const { data } = result;
          resolve({
            status: true,
            message: data.message || ""
          });
        })
        .catch((error) => {
          let dataErrors = [];
          if (error.response) {
            dataErrors = error.response.data;
          }
          reject({
            status: false,
            message: error.message || "",
            dataError: dataErrors
          });
        });
    });
  };

  // ** Reset Password
  const resetPassword = ({ formData }) => {
    return new Promise((resolve, reject) => {
      AuthServices.resetPassword({ formData: formData })
        .then((result) => {
          const { data } = result;
          saveToken({
            access_token: data.data.access_token,
            token_type: data.data.token_type
          });
          resolve({
            status: true,
            message: data.message || ""
          });
        })
        .catch((error) => {
          let dataErrors = {};
          if (error.response) {
            if (typeof error.response.data.message === "string") {
              dataErrors = { message: { code: [error.response.data.message] } };
            } else {
              dataErrors = error.response.data;
            }
          }
          reject({
            status: false,
            message: error.message || "",
            dataError: dataErrors
          });
        });
    });
  };

  useEffect(() => {
    if (Object.keys(userSecure).length > 0) {
      getProfileData(userSecure?.access_token);
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSecure]);

  useEffect(() => {
    // if (Object.keys(userSecure).length > 0) {
    //   // // ** Set Header Authorization public Token
    //   apiFetech.interceptors.request.use(
    //     async function (config) {
    //       // Do something before request is sent
    //       config.headers.common[
    //         "Authorization"
    //       ] = `Bearer ${userSecure?.access_token}`;
    //      // config.headers.common["unique_code_guest"] = await makeUniqueCode();
    //       return config;
    //     },
    //     function (error) {
    //       // Do something with request error
    //       return Promise.reject(error);
    //     }
    //   );
    // } else {

    // }
    // ** Set Header Authorization public Token && unique code guest
    apiFetech.interceptors.request.use(
      async function (config) {
        // Do something before request is sent
        const unique_code_guest = await makeUniqueCode();
        if (unique_code_guest) {
          config.headers.common["unique_code_guest"] = unique_code_guest;
        }
        if (Object.keys(userSecure).length > 0) {
          config.headers.common[
            "Authorization"
          ] = `Bearer ${userSecure?.access_token}`;
        }
        return config;
      },
      function (error) {
        // Do something with request error
        return Promise.reject(error);
      }
    );
    return () => {};
  }, [userSecure]);

  // Get Location User
  useEffect(() => {
    var globalLatLog = {
      lat: 29.971672080709897,
      long: 31.255079004477068
    };
      try {
        getClientLocation()
          .then(async (result) => {
            const { currentPosition } = await result;
            globalLatLog = {...currentPosition};
            if (Object.keys(currentPosition).length > 0) {
              saveUserLocation(currentPosition);
              await getInfoFromGoogleMap({
                latlong: `${currentPosition.lat},${currentPosition.long}`,
                language: `${language}`
              })
                .then(async (resultMap) => {
                  const { cleanData } = await resultMap;
                  clientSaveLocation({
                    cleanData: cleanData,
                    currentPosition: currentPosition
                  });
                })
                .catch((error) => {
                  const currentPosition = {
                        lat: 29.971672080709897,
                        long: 31.255079004477068
                      };
                      const cleanData = {
                      street_address: { formatted_address: "" },
                      route: { formatted_address: "" },
                      locality: { formatted_address: "" },
                      administrative_area_level_1: { formatted_address: "" },
                      country: { formatted_address: "" }
                    };
                  clientSaveLocation({
                    cleanData: cleanData,
                    currentPosition: currentPosition
                  });
                });
            }
          })
          .catch((error) => {
            const cleanData = {
              street_address: { formatted_address: "" },
              route: { formatted_address: "" },
              locality: { formatted_address: "" },
              administrative_area_level_1: { formatted_address: "" },
              country: { formatted_address: "" }
            };
            const currentPosition = {
              lat: userData?.local_lat ?? globalLatLog.lat,
              long: userData?.local_long ?? globalLatLog.long
            };
            if (Object.keys(userData).length > 0) {
              cleanData.street_address.formatted_address = "";
              cleanData.route.formatted_address = "";
              cleanData.locality.formatted_address = "";
              cleanData.administrative_area_level_1.formatted_address = "";
              cleanData.country.formatted_address = userData?.country.name || "";
            }
            clientSaveLocation({
              cleanData: cleanData,
              currentPosition: currentPosition
            });
            console.log("error", error);
          });
      } catch (error) {
        if (!routesNotRedirectNotSupported.includes(location.pathname)) {
            navigate("/not-supported");
        }
      }
    // }

    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ** Is Country Supported
  useEffect(() => {
    /**
     * **********************************************************
     * 
     *  Checked this code error the selected address in checkout screen
     * 
     * **********************************************************
     */
    try {
    if(isStartGetLocation){
      if (
        "cityInfo" in userLocation &&
        countries.length > 0 &&
        cities.length > 0
      ) {
        const { fullAddress,cityInfo } = userLocation;
        const cityObj = { name: null };
        if(Object.keys(cityInfo).length > 0){
  
        }else{
          if (fullAddress?.route) {
            cityObj.name = fullAddress?.route;
          } else if (fullAddress?.locality) {
            cityObj.name = fullAddress?.locality;
          } else if (fullAddress?.street_address) {
            cityObj.name = fullAddress?.street_address;
          } else if (fullAddress?.administrative_area_level_1) {
            cityObj.name = fullAddress?.administrative_area_level_1;
          }
        }
  
        if(Object.keys(userLocation.cityInfo).length > 0){
          cityObj.name = userLocation.cityInfo.long_name || cityObj.name;
        }
  
        const country = countries.find(
          (country) =>
            country?.name?.toUpperCase() === fullAddress?.country?.toUpperCase()
        );
        const city = cities.find((c) => {
          const cityName = cityObj.name;
          return cityName.toUpperCase().includes(c?.name?.toUpperCase());
        });
        if ((!country && !city) && localStorage.getItem('Onboarding') != null) {
          if (!routesNotRedirectNotSupported.includes(location.pathname)) {
              navigate("/not-supported");
          }
          // setIsSupportedAppLoading(false);
        } else {
          saveClientCity(city);
        }
        setIsSupportedAppLoading(false);
      }
      // setIsSupportedAppLoading(false);

    }
    } catch (error) {
      if (!routesNotRedirectNotSupported.includes(location.pathname)) {
          navigate("/not-supported");
      }
    }

    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStartGetLocation]);

  useEffect(() => {
    if (
      "cityInfo" in userLocation &&
      countries.length > 0 &&
      cities.length > 0
    ){
      setIsStartGetLocation(true)
    }
  
    return () => {
      setIsStartGetLocation(false)
    }
  }, [cities.length, countries.length, userLocation])
  

  // ** ComponentDidMount
  useEffect(() => {
    setUserData({});
    const unsubscribe = onAuthStateChanged((user) => {
      if (user) {
        getToken();
        // ** Add to user to state
        setUserData(user);
      } else {
        setUserData({});
      }
    });
    // ** Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    const data = localStorage.getItem("secure")
      ? JSON.parse(localStorage.getItem("secure"))
      : {};
    setUserSecure(data);

    return () => {};
  }, []);

  useEffect(() => {
    makeUniqueCode();
    return () => {};
  }, []);

  useEffect(() => {
    if(userLocation && 'cityInfo' in userLocation ) {
        if(cities.length <= 0){
          getCoutries()
        }
      }
    return () => {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLocation])

  useEffect(() => {
    if( localStorage.getItem('Onboarding') &&  Object.keys(userSecure).length >= 0){
      if(!window.location.pathname.includes("order-success")){
        navigate('/login')
      }
    }
    return () => {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSecure])

  useEffect(() => {
    apiFetech.interceptors.response.use(function (response) {
      return response;
  }, function (error) {
      if (401 === error.response.status) {
        localStorage.removeItem("user");
        localStorage.removeItem("secure");
        setUserData({});
        navigate("/login");
      } else {
          return Promise.reject(error);
      }
  });
    return () => {
      
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  

  return {
    userData,
    userSecure,
    userLocation,
    IsSupportedAppLoading,
    setUserSecure,
    setUserData,
    handleLogin,
    handleSignOut,
    signUp,
    saveToken,
    saveUser,
    sedMessage,
    setUserLocation,
    clientSaveLocation,
    appIsCitySupported,
    updateProfile,
    makeUniqueCode,
    forgetPassword,
    resetPassword
  };
}
